import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Collapse,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Tooltip,
  alpha,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Fade,
  Tabs,
  Tab,
  Fab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  FileDownload,
  Refresh,
  KeyboardArrowDown,
  KeyboardArrowUp,
  FilterList,
  LocalShipping,
  TrendingUp,
  CheckCircle,
  Close,
  Download,
  ArrowForward,
  ShoppingCart,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

// ==================== THEME CONFIGURATION ====================
const PRIMARY_COLOR = '#004D00';
const SECONDARY_COLOR = '#d9f2d9';
const HOVER_COLOR = '#e6f2e6';
const ALTERNATING_COLOR = '#f5f5f5';

const customTheme = createTheme({
  palette: {
    primary: {
      main: PRIMARY_COLOR,
    },
    secondary: {
      main: '#00a652',
    },
    background: {
      default: '#fafbfc',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    fontSize: 12,
    h6: { fontSize: '1rem', fontWeight: 700 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.75rem' },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          padding: '10px 12px',
          borderBottom: '1px solid #e0e0e0',
        },
        head: {
          fontWeight: 700,
          backgroundColor: PRIMARY_COLOR,
          color: 'white',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

// ==================== STYLED COMPONENTS ====================
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: HOVER_COLOR,
  },
  '&.selected': {
    backgroundColor: SECONDARY_COLOR,
  },
}));

const IndentDetailsRow = styled(TableRow)({
  '&:nth-of-type(odd)': { backgroundColor: '#ffffff' },
  '&:nth-of-type(even)': { backgroundColor: ALTERNATING_COLOR },
  '&:hover': { backgroundColor: HOVER_COLOR },
});

// ==================== FILTER COMPONENT ====================
const FilterBar = ({ filters, onFilterChange, onApply, isLoading, areas = [], loadingAreas = false }) => (
  <Card sx={{ borderRadius: 2, mb: 1.5 }}>
    <CardHeader
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <FilterList sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Filter & Search</Typography>
        </Box>
      }
      sx={{ pb: 0.8, pt: 1, px: 1.5 }}
    />
    <Divider />
    <CardContent sx={{ py: 1.2, px: 1.5 }}>
      <Grid container spacing={1.5} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" disabled={loadingAreas}>
            <InputLabel sx={{ fontSize: '0.85rem' }}>Area</InputLabel>
            <Select
              value={filters.area}
              label="Area"
              onChange={(e) => onFilterChange('area', e.target.value)}
            >
              {loadingAreas ? (
                <MenuItem disabled>Loading areas...</MenuItem>
              ) : areas.length > 0 ? (
                areas.map((area) => (
                  <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                    {area.SFAreaName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No areas available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="From Date"
            value={filters.fromDate}
            onChange={(newValue) => onFilterChange('fromDate', newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="To Date"
            value={filters.toDate}
            onChange={(newValue) => onFilterChange('toDate', newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="contained"
            fullWidth
            size="small"
            onClick={onApply}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh sx={{ fontSize: '1rem' }} />}
            sx={{ py: 0.75, fontSize: '0.8rem' }}
          >
            {isLoading ? 'Loading...' : 'Apply'}
          </Button>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// ==================== INDENT DETAILS COMPONENT ====================
const IndentDetailsTable = ({ indent }) => {
  const lines = Array.isArray(indent.line) ? indent.line : [];
  const totalQty = parseFloat(lines.reduce((sum, l) => sum + (l.Qty || 0), 0).toFixed(2));

  return (
    <Box sx={{ my: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: PRIMARY_COLOR }}>
        Items in Indent {indent.IndentID}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.1) }}>
              <TableCell>Item ID</TableCell>
              <TableCell>Item Name</TableCell>
              <TableCell align="right">Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No items in this indent
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              lines.map((line, idx) => (
                <IndentDetailsRow key={idx}>
                  <TableCell sx={{ fontWeight: 600 }}>{line.ItemID}</TableCell>
                  <TableCell>{line.ItemName}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {line.Qty}
                  </TableCell>
                </IndentDetailsRow>
              ))
            )}
            <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.05) }}>
              <TableCell colSpan={2} sx={{ fontWeight: 700, textAlign: 'right' }}>
                Total Qty:
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {totalQty}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ==================== BRANCH ROW COMPONENT ====================
const BranchRowCollapsible = ({ branch, selectedIndents, onSelectIndent, onSelectAllIndents, expandedBranch, onExpandBranch, expandedIndent, onExpandIndent, selectedItems, onSelectItem, onSelectMultipleItems }) => {
  const docs = Array.isArray(branch.doc) ? branch.doc : [];
  // Use BranchId from each indent doc itself, not the parent branch
  const branchIndentIds = docs.map(d => `${d.BranchId || branch.BranchId}|${d.IndentID}`);
  const branchSelectedCount = branchIndentIds.filter(id => selectedIndents.includes(id)).length;
  const totalBranchQty = parseFloat(docs.reduce((sum, d) => {
    const lines = Array.isArray(d.line) ? d.line : [];
    return sum + lines.reduce((ls, l) => ls + (l.Qty || 0), 0);
  }, 0).toFixed(2));

  // Calculate total items in branch and how many are selected
  const allBranchItems = [];
  docs.forEach((indent) => {
    // Use BranchId from the indent doc itself, not the parent branch
    const indentBranchId = indent.BranchId || branch.BranchId;
    const indentKey = `${indentBranchId}|${indent.IndentID}`;
    const lines = Array.isArray(indent.line) ? indent.line : [];
    lines.forEach((line) => {
      allBranchItems.push(`${indentKey}|${line.ItemID}`);
    });
  });
  const branchSelectedItemsCount = allBranchItems.filter(itemKey => selectedItems[itemKey]).length;

  const isExpandedBranch = expandedBranch === branch.BranchId;
  const areBranchIndentsAllSelected = branchIndentIds.length > 0 && branchIndentIds.every(id => selectedIndents.includes(id));
  const areBranchItemsAllSelected = allBranchItems.length > 0 && branchSelectedItemsCount === allBranchItems.length;
  const isBranchPartiallySelected = branchSelectedItemsCount > 0 && branchSelectedItemsCount < allBranchItems.length;

  // Handler to select/deselect all indents in this branch
  const handleBranchSelectAll = useCallback((e) => {
    e.stopPropagation();
    const isSelecting = !areBranchItemsAllSelected;

    if (isSelecting) {
      // Select all indents in this branch
      const newSelectedIndents = Array.from(new Set([...selectedIndents, ...branchIndentIds]));
      onSelectAllIndents(newSelectedIndents);

      // Also select all items in all indents of this branch
      const itemUpdates = {};
      docs.forEach((indent) => {
        const indentKey = `${branch.BranchId}|${indent.IndentID}`;
        const lines = Array.isArray(indent.line) ? indent.line : [];
        lines.forEach((line) => {
          itemUpdates[`${indentKey}|${line.ItemID}`] = true;
        });
      });
      onSelectMultipleItems(itemUpdates);
    } else {
      // Deselect all indents in this branch
      const newSelectedIndents = selectedIndents.filter(id => !branchIndentIds.includes(id));
      onSelectAllIndents(newSelectedIndents);

      // Also deselect all items in all indents of this branch
      const itemUpdates = {};
      docs.forEach((indent) => {
        const indentKey = `${branch.BranchId}|${indent.IndentID}`;
        const lines = Array.isArray(indent.line) ? indent.line : [];
        lines.forEach((line) => {
          itemUpdates[`${indentKey}|${line.ItemID}`] = false;
        });
      });
      onSelectMultipleItems(itemUpdates);
    }
  }, [areBranchItemsAllSelected, branchIndentIds, selectedIndents, onSelectAllIndents, docs, branch.BranchId, onSelectMultipleItems, selectedItems, allBranchItems]);

  return (
    <React.Fragment>
      {/* Branch Summary Row */}
      <StyledTableRow
        onClick={() => onExpandBranch(isExpandedBranch ? null : branch.BranchId)}
        sx={{
          backgroundColor: isExpandedBranch ? alpha(PRIMARY_COLOR, 0.05) : 'transparent',
          '&:hover': { backgroundColor: alpha(PRIMARY_COLOR, 0.08) }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: 40 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Checkbox
              size="small"
              checked={areBranchItemsAllSelected}
              indeterminate={isBranchPartiallySelected}
              onChange={handleBranchSelectAll}
              sx={{
                color: PRIMARY_COLOR,
                '&.Mui-checked': { color: PRIMARY_COLOR },
                '&.MuiCheckbox-indeterminate': { color: PRIMARY_COLOR }
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onExpandBranch(isExpandedBranch ? null : branch.BranchId);
              }}
            >
              {isExpandedBranch ? <KeyboardArrowUp sx={{ fontSize: '1rem' }} /> : <KeyboardArrowDown sx={{ fontSize: '1rem' }} />}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 32, height: 32, backgroundColor: alpha(PRIMARY_COLOR, 0.1), color: PRIMARY_COLOR, fontSize: '0.75rem' }}>
              {String(branch.BranchName || 'B').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {branch.BranchName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {branch.BranchId}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={docs.length}
            size="small"
            variant="outlined"
            sx={{ minWidth: 40 }}
          />
        </TableCell>
        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end" gap={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {branchSelectedCount}/{branchIndentIds.length}
            </Typography>
            <Chip
              label={totalBranchQty}
              size="small"
              sx={{
                backgroundColor: alpha(PRIMARY_COLOR, 0.12),
                color: PRIMARY_COLOR,
                fontWeight: 700,
                minWidth: 50,
              }}
            />
          </Box>
        </TableCell>
      </StyledTableRow>

      {/* Expand to show Indents */}
      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
          <Collapse in={isExpandedBranch} timeout="auto" unmountOnExit>
            <Box sx={{
              bgcolor: alpha(PRIMARY_COLOR, 0.02),
              p: 2,
              borderLeft: `4px solid ${PRIMARY_COLOR}`
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: PRIMARY_COLOR }}>
                Indents for {branch.BranchName}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Indent ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">Items</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">Qty</TableCell>
                    <TableCell sx={{ width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docs.map((indent) => {
                    // Use BranchId from the indent doc itself, not the parent branch
                    const indentBranchId = indent.BranchId || branch.BranchId;
                    const indentKey = `${indentBranchId}|${indent.IndentID}`;
                    // Create unique key with date to distinguish same IndentID from different dates
                    const indentDateString = dayjs(indent.IndentDate).format('YYYYMMDD');
                    const uniqueIndentKey = `${indentKey}|${indentDateString}`;
                    const isSelected = selectedIndents.includes(indentKey);
                    const isExpandedIndent = expandedIndent === indentKey;
                    const lines = Array.isArray(indent.line) ? indent.line : [];
                    const totalQty = parseFloat(lines.reduce((sum, l) => sum + (l.Qty || 0), 0).toFixed(2));

                    // Get UOM - use first line's UOM or find most common
                    const primaryUOM = lines.length > 0 ? (lines[0].UOM || 'EA') : 'EA';

                    // Calculate if any items in this indent are selected
                    const indentItemsSelected = lines.filter((line) =>
                      selectedItems[`${indentKey}|${line.ItemID}`]
                    ).length;
                    const hasPartialSelection = indentItemsSelected > 0 && indentItemsSelected < lines.length;
                    const allItemsSelected = indentItemsSelected === lines.length && lines.length > 0;

                    return (
                      <React.Fragment key={uniqueIndentKey}>
                        <IndentDetailsRow sx={{
                          backgroundColor: isSelected || allItemsSelected || hasPartialSelection ? alpha(PRIMARY_COLOR, 0.08) : 'transparent',
                          '&:hover': { backgroundColor: alpha(PRIMARY_COLOR, 0.05) }
                        }}>
                          <TableCell padding="checkbox" sx={{ width: 'auto', p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                              <Checkbox
                                size="small"
                                checked={isSelected || allItemsSelected}
                                indeterminate={hasPartialSelection}
                                onChange={(e) => onSelectIndent(indentKey, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0.5 }}
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onExpandIndent(isExpandedIndent ? null : indentKey);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                {isExpandedIndent ? (
                                  <KeyboardArrowUp sx={{ fontSize: '0.9rem', color: PRIMARY_COLOR }} />
                                ) : (
                                  <KeyboardArrowDown sx={{ fontSize: '0.9rem', color: PRIMARY_COLOR }} />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {indent.IndentID}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {dayjs(indent.IndentDate).format('DD-MMM')}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                            {lines.length}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${totalQty} ${primaryUOM}`}
                              size="small"
                              sx={{
                                backgroundColor: alpha(PRIMARY_COLOR, 0.1),
                                color: PRIMARY_COLOR,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              }}
                            />
                          </TableCell>
                        </IndentDetailsRow>

                        {/* Item Details Row */}
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={isExpandedIndent} timeout="auto" unmountOnExit>
                              <Box sx={{
                                bgcolor: 'white',
                                p: 1,
                                borderLeft: `3px solid ${alpha(PRIMARY_COLOR, 0.3)}`,
                                ml: 2,
                                maxHeight: '200px',
                                overflowY: 'auto'
                              }}>
                                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.05) }}>
                                      <TableCell sx={{ width: '40px' }} padding="checkbox">
                                        <Checkbox
                                          size="small"
                                          checked={lines.length > 0 && lines.every((line) =>
                                            selectedItems[`${indentKey}|${line.ItemID}`]
                                          )}
                                          indeterminate={
                                            lines.some((line) => selectedItems[`${indentKey}|${line.ItemID}`]) &&
                                            !lines.every((line) => selectedItems[`${indentKey}|${line.ItemID}`])
                                          }
                                          onChange={(e) => {
                                            const allItemKeys = lines.map((line) => `${indentKey}|${line.ItemID}`);
                                            const newState = {};
                                            allItemKeys.forEach((key) => {
                                              newState[key] = e.target.checked;
                                            });
                                            // Update all items at once using the batch handler
                                            onSelectMultipleItems(newState);
                                          }}
                                          sx={{ p: 0.5 }}
                                           disabled 
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Item ID</TableCell>
                                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Item Name</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Qty (UOM)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {lines.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            No items
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      lines.map((line, idx) => {
                                        const itemKey = `${indentKey}|${line.ItemID}`;
                                        // Use indent date + line number for truly unique key
                                        const uniqueKey = `${uniqueIndentKey}|${line.LineNumber || idx}`;
                                        return (
                                          <TableRow key={uniqueKey} sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: '#fff' },
                                            '&:nth-of-type(even)': { backgroundColor: ALTERNATING_COLOR },
                                            '&:hover': { backgroundColor: HOVER_COLOR }
                                          }}>
                                            <TableCell padding="checkbox">
                                              <Checkbox
                                                size="small"
                                                checked={selectedItems[itemKey] || false}
                                                onChange={(e) => onSelectItem(itemKey, e.target.checked)}
                                                sx={{ p: 0.5 }}
                                                disabled
                                              />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                              {line.ItemID}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.7rem' }}>
                                              {line.ItemName}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                              {line.Qty} {line.UOM || 'EA'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

// ==================== INDENT SUMMARY TABLE COMPONENT ====================
const IndentSummaryTable = ({
  branches,
  selectedIndents,
  onSelectIndent,
  onSelectAllIndents,
  expandedIndent,
  onExpandIndent,
  selectedItems,
  onSelectItem,
  onSelectMultipleItems,
}) => {
  const [expandedBranch, setExpandedBranch] = useState(null);

  // Restructure branches by doc.BranchId to group indents by their actual branch
  const restructuredBranches = useMemo(() => {
    const branchMap = new Map();

    branches.forEach((parentBranch) => {
      const docs = Array.isArray(parentBranch.doc) ? parentBranch.doc : [];
      docs.forEach((doc) => {
        // Use BranchId from the doc (e.g., L203, L205)
        const docBranchId = doc.BranchId;
        if (!branchMap.has(docBranchId)) {
          branchMap.set(docBranchId, {
            BranchId: docBranchId,
            BranchName: parentBranch.BranchName, // Keep parent branch name (or you can get it from another source)
            doc: []
          });
        }
        branchMap.get(docBranchId).doc.push(doc);
      });
    });

    return Array.from(branchMap.values());
  }, [branches]);

  const allIndentIds = useMemo(() => {
    const ids = [];
    restructuredBranches.forEach((branch) => {
      const docs = Array.isArray(branch.doc) ? branch.doc : [];
      docs.forEach((indent) => {
        // Use BranchId from the branch (which is already doc.BranchId)
        ids.push(`${branch.BranchId}|${indent.IndentID}`);
      });
    });
    return ids;
  }, [restructuredBranches]);

  // Calculate total items across all branches and how many are selected
  const allItems = useMemo(() => {
    const items = [];
    restructuredBranches.forEach((branch) => {
      const docs = Array.isArray(branch.doc) ? branch.doc : [];
      docs.forEach((indent) => {
        // Use BranchId from the branch (which is already doc.BranchId)
        const indentKey = `${branch.BranchId}|${indent.IndentID}`;
        const lines = Array.isArray(indent.line) ? indent.line : [];
        lines.forEach((line) => {
          items.push(`${indentKey}|${line.ItemID}`);
        });
      });
    });
    return items;
  }, [restructuredBranches]);

  const totalSelectedItems = useMemo(() => {
    return allItems.filter(itemKey => selectedItems[itemKey]).length;
  }, [allItems, selectedItems]);

  const areAllItemsSelected = allItems.length > 0 && totalSelectedItems === allItems.length;
  const isSomeItemsSelected = totalSelectedItems > 0 && totalSelectedItems < allItems.length;

  return (
    <Card sx={{
      borderRadius: 2,
      mb: 1.5,
      display: 'flex',
      flexDirection: 'column',
      height: '450px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 12px rgba(0, 77, 0, 0.08)'
    }}>
      {/* <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1.2}>
            <Box sx={{
              backgroundColor: alpha(PRIMARY_COLOR, 0.1),
              borderRadius: '6px',
              p: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LocalShipping sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY_COLOR, fontSize: '0.95rem' }}>
                Indent Consolidation
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Select indents from branches
              </Typography>
            </Box>
          </Box>
        }
        action={
          <Chip
            label={`${selectedIndents.length} Selected`}
            size="small"
            sx={{
              backgroundColor: selectedIndents.length > 0 ? PRIMARY_COLOR : '#e0e0e0',
              color: selectedIndents.length > 0 ? 'white' : '#666',
              fontWeight: 700,
              height: 24,
              fontSize: '0.75rem'
            }}
          />
        }
        sx={{ pb: 0.8, pt: 1, px: 1.5, backgroundColor: alpha(PRIMARY_COLOR, 0.02) }}
      /> */}
      <Divider />
      <CardContent sx={{
        p: 0,
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '5px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: PRIMARY_COLOR,
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: alpha(PRIMARY_COLOR, 0.8),
          },
        },
      }}>
        <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: PRIMARY_COLOR }}>
              <TableCell padding="checkbox" sx={{ width: 40, backgroundColor: PRIMARY_COLOR }}>
                <Checkbox
                  checked={areAllItemsSelected}
                  indeterminate={isSomeItemsSelected}
                  onChange={(e) => {
                    console.log('📋 Header Checkbox Changed:', { checked: e.target.checked });
                    if (e.target.checked) {
                      onSelectAllIndents(allIndentIds);
                      // Also select all items across all branches and indents
                      const itemUpdates = {};
                      branches.forEach((branch) => {
                        const docs = Array.isArray(branch.doc) ? branch.doc : [];
                        docs.forEach((indent) => {
                          // IMPORTANT: Use indent.BranchId, not branch.BranchId
                          const indentBranchId = indent.BranchId || branch.BranchId;
                          const indentKey = `${indentBranchId}|${indent.IndentID}`;
                          const lines = Array.isArray(indent.line) ? indent.line : [];
                          lines.forEach((line) => {
                            itemUpdates[`${indentKey}|${line.ItemID}`] = true;
                          });
                        });
                      });
                      console.log('✅ Selecting all items:', { itemCount: Object.keys(itemUpdates).length, itemUpdates });
                      onSelectMultipleItems(itemUpdates);
                    } else {
                      onSelectAllIndents([]);
                      // Also deselect all items
                      const itemUpdates = {};
                      branches.forEach((branch) => {
                        const docs = Array.isArray(branch.doc) ? branch.doc : [];
                        docs.forEach((indent) => {
                          // IMPORTANT: Use indent.BranchId, not branch.BranchId
                          const indentBranchId = indent.BranchId || branch.BranchId;
                          const indentKey = `${indentBranchId}|${indent.IndentID}`;
                          const lines = Array.isArray(indent.line) ? indent.line : [];
                          lines.forEach((line) => {
                            itemUpdates[`${indentKey}|${line.ItemID}`] = false;
                          });
                        });
                      });
                      console.log('❌ Deselecting all items:', { itemCount: Object.keys(itemUpdates).length, itemUpdates });
                      onSelectMultipleItems(itemUpdates);
                    }
                  }}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                    '&.MuiCheckbox-indeterminate': { color: 'white' }
                  }}
                />
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>
                Branch
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }} align="center">
                Indents
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }} align="right">
                Selected / Qty
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No indents found. Apply filters to load data.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              restructuredBranches.map((branch) => (
                <BranchRowCollapsible
                  key={branch.BranchId}
                  branch={branch}
                  selectedIndents={selectedIndents}
                  onSelectIndent={onSelectIndent}
                  onSelectAllIndents={onSelectAllIndents}
                  expandedBranch={expandedBranch}
                  onExpandBranch={setExpandedBranch}
                  expandedIndent={expandedIndent}
                  onExpandIndent={onExpandIndent}
                  selectedItems={selectedItems}
                  onSelectItem={onSelectItem}
                  onSelectMultipleItems={onSelectMultipleItems}
                />
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ==================== CONSOLIDATED SUMMARY COMPONENT ====================
const ConsolidatedSummarySection = ({
  selectedIndents,
  branches,
  onPlaceOrder,
  isPlacingOrder,
  onSelectIndent,
  selectedItems,
  onSelectItem,
  onFetchPOList,
  loadingPOList,
  vendorList = [],
}) => {
  console.log('📈 ConsolidatedSummarySection rendered:', {
    selectedIndentsCount: selectedIndents.length,
    selectedIndents,
    selectedItemsCount: Object.keys(selectedItems).length,
    selectedItemsKeys: Object.keys(selectedItems),
    branchesCount: branches.length
  });

  const [expandedItemId, setExpandedItemId] = useState(null);
  const [excessQties, setExcessQties] = useState({});
  const [vendorIds, setVendorIds] = useState({});
  const [editingVendor, setEditingVendor] = useState(null);
  const [rates, setRates] = useState({});
  const [taxRates, setTaxRates] = useState({});
  const [taxCategoryIds, setTaxCategoryIds] = useState({});
  const [taxStructureCodes, setTaxStructureCodes] = useState({});
  const [editingRate, setEditingRate] = useState(null);
  const [editingTax, setEditingTax] = useState(null);

  const consolidatedItems = useMemo(() => {
    console.log('💡 Calculating consolidatedItems...', {
      selectedIndentsCount: selectedIndents.length,
      selectedIndents: selectedIndents,
      selectedItemsCount: Object.keys(selectedItems).length
    });
    const itemMap = new Map();

    selectedIndents.forEach((indentKey) => {
      const [indentBranchId, indentId] = indentKey.split('|');
      console.log('🔍 Processing indent:', { indentKey, indentBranchId, indentId });

      // Find the indent in any branch by searching through all branches and their docs
      let foundIndent = null;
      let foundBranch = null;
      let docBranchId = indentBranchId;

      // Search through all branches to find the indent
      // IMPORTANT: Match by BOTH BranchId AND IndentID to find the correct indent
      for (const branch of branches) {
        const docs = Array.isArray(branch.doc) ? branch.doc : [];
        for (const doc of docs) {
          // Match by BOTH BranchId AND IndentID
          if (doc.BranchId === indentBranchId && doc.IndentID === indentId) {
            foundIndent = doc;
            foundBranch = branch;
            docBranchId = doc.BranchId;
            break;
          }
        }
        if (foundIndent) break;
      }

      if (!foundIndent || !foundBranch) {
        console.warn('⚠️ Indent not found in branches:', { indentKey, indentBranchId, indentId });
        return;
      }

      const lines = Array.isArray(foundIndent.line) ? foundIndent.line : [];

      console.log('✅ Found indent:', {
        IndentID: foundIndent.IndentID,
        docBranchId,
        itemCount: lines.length,
        actualItems: lines.map(l => ({
          ItemID: l.ItemID,
          ItemName: l.ItemName,
          Qty: l.Qty
        }))
      });
      lines.forEach((line) => {
        // IMPORTANT: Use the ORIGINAL indentKey from selectedIndents, not the reconstructed one
        // The indentKey from selectedIndents is what was used when the item was selected
        const itemKey = `${indentKey}|${line.ItemID}`;
        const isItemSelected = selectedItems[itemKey];

        console.log('🔎 Checking item:', {
          itemKey,
          originalIndentKey: indentKey,
          docBranchId,
          isItemSelected,
          lineItemID: line.ItemID
        });

        if (!isItemSelected) {
          console.log('⏭️ Skipping unselected item:', itemKey);
          return;
        }

        console.log('✅ Including item:', { itemKey, ItemID: line.ItemID, ItemName: line.ItemName });

        const key = line.ItemID;
        if (itemMap.has(key)) {
          const existing = itemMap.get(key);
          existing.totalQty += line.Qty || 0;
          existing.branches.add(docBranchId);
          // Store indent info for drill-down - use ORIGINAL indentKey for consistency
          existing.indents.push({
            IndentID: foundIndent.IndentID,
            BranchId: docBranchId,
            BranchName: foundBranch.BranchName,
            Qty: line.Qty || 0,
            indentKey: indentKey, // Use the original indentKey from selectedIndents
            IndentDate: foundIndent.IndentDate,
            UOM: line.UOM || 'EA'
          });
        } else {
          itemMap.set(key, {
            ItemID: line.ItemID,
            ItemName: line.ItemName,
            UOM: line.UOM || 'EA',
            totalQty: line.Qty || 0,
            branches: new Set([docBranchId]),
            indents: [{
              IndentID: foundIndent.IndentID,
              BranchId: docBranchId,
              BranchName: foundBranch.BranchName,
              Qty: line.Qty || 0,
              indentKey: indentKey, // Use the original indentKey from selectedIndents
              IndentDate: foundIndent.IndentDate,
              UOM: line.UOM || 'EA'
            }]
          });
        }
      });
    });

    const result = Array.from(itemMap.values());
    const consolidatedTotalQty = result.reduce((sum, item) => sum + item.totalQty, 0);

    console.log('✨ CONSOLIDATED ITEMS CALCULATED:', {
      totalUniqueItems: result.length,
      totalConsolidatedQty: consolidatedTotalQty,
      itemsBreakdown: result.map(item => ({
        ItemID: item.ItemID,
        ItemName: item.ItemName,
        totalQty: item.totalQty,
        fromIndents: item.indents.length,
        indentDetails: item.indents.map(i => ({
          indentKey: i.indentKey,
          IndentID: i.IndentID,
          BranchId: i.BranchId,
          qty: i.Qty
        }))
      }))
    });

    console.log('🎯 CONSOLIDATED VS SELECTED COMPARISON:', {
      selectedItems: Object.keys(selectedItems).filter(k => selectedItems[k]).length,
      consolidatedUniqueItems: result.length,
      note: 'Same ItemID from different indents are merged into one row'
    });

    return result;
  }, [selectedIndents, branches, selectedItems]);

  const totalConsolidatedQty = useMemo(
    () => parseFloat(consolidatedItems.reduce((sum, item) => sum + item.totalQty, 0).toFixed(2)),
    [consolidatedItems]
  );

  // Auto-select vendor if only one option exists
  React.useEffect(() => {
    if (vendorList.length === 1 && consolidatedItems.length > 0) {
      const singleVendorId = vendorList[0].customerCode || vendorList[0].branchID;
      // Set this vendor for all items that don't already have a vendor
      setVendorIds(prev => {
        const autoVendorIds = { ...prev };
        consolidatedItems.forEach((item) => {
          if (!autoVendorIds[item.ItemID]) {
            autoVendorIds[item.ItemID] = singleVendorId;
          }
        });
        return autoVendorIds;
      });
    }
  }, [vendorList, consolidatedItems]);

  return (
    <Card sx={{
      borderRadius: 2,
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 16px rgba(0, 77, 0, 0.12)',
      border: `2px solid ${alpha(PRIMARY_COLOR, 0.3)}`
    }}>
      
      <Divider />
      <CardContent>
        {consolidatedItems.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 6,
            backgroundColor: alpha(PRIMARY_COLOR, 0.02),
            borderRadius: '8px'
          }}>
            <LocalShipping sx={{ fontSize: 48, color: alpha(PRIMARY_COLOR, 0.3), mb: 1 }} />
            <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              Select indents above to view consolidated items
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click on a branch and select one or more indents to consolidate
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Stats */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                  p: 1.2,
                  borderRadius: '8px',
                  borderLeft: `3px solid ${PRIMARY_COLOR}`
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    UNIQUE ITEMS
                  </Typography>
                  <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700, mt: 0.25, fontSize: '1.1rem' }}>
                    {consolidatedItems.length}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                  p: 1.2,
                  borderRadius: '8px',
                  borderLeft: `3px solid ${PRIMARY_COLOR}`
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    TOTAL QUANTITY
                  </Typography>
                  <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700, mt: 0.25, fontSize: '1.1rem' }}>
                    {totalConsolidatedQty}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                  p: 1.2,
                  borderRadius: '8px',
                  borderLeft: `3px solid ${PRIMARY_COLOR}`
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    INDENTS SELECTED
                  </Typography>
                  <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700, mt: 0.25, fontSize: '1.1rem' }}>
                    {selectedIndents.length}
                  </Typography>
                </Box>
              </Grid>
             
            </Grid>

            <Divider sx={{ mb: 1.5 }} />

            {/* Consolidated Items Table */}
            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, color: PRIMARY_COLOR, display: 'block', fontSize: '0.8rem' }}>
              Consolidated Items
            </Typography>
            <TableContainer sx={{ mb: 1.5, maxHeight: '350px', overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 0.75 } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: PRIMARY_COLOR }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', width: '5%' }} />
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Item ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Item Name</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Indented Qty (UOM)</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Adjustment Qty (UOM)</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Final Qty (UOM)</TableCell>
                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Rate</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Tax Rate (%)</TableCell> */}
                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Total Amount</TableCell> */}
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Vendor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consolidatedItems.map((item, idx) => {
                    // Create unique key using first indent's ID and date
                    const firstIndent = item.indents && item.indents.length > 0 ? item.indents[0] : null;
                    const indentDateString = firstIndent ? dayjs(firstIndent.IndentDate || new Date()).format('YYYYMMDD') : 'unknown';
                    const uniqueConsolidatedKey = `${item.ItemID}|${firstIndent?.IndentID || 'noindent'}|${indentDateString}-${idx}`;
                    return (
                    <React.Fragment key={uniqueConsolidatedKey}>
                      <TableRow sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fff' },
                        '&:nth-of-type(even)': { backgroundColor: ALTERNATING_COLOR },
                        '&:hover': { backgroundColor: HOVER_COLOR }
                      }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: '5%' }}>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedItemId(expandedItemId === item.ItemID ? null : item.ItemID)}
                            sx={{ p: 0 }}
                          >
                            {expandedItemId === item.ItemID ? (
                              <KeyboardArrowUp sx={{ fontSize: '1rem', color: PRIMARY_COLOR }} />
                            ) : (
                              <KeyboardArrowDown sx={{ fontSize: '1rem', color: PRIMARY_COLOR }} />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          <Chip
                            label={item.ItemID}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{item.ItemName}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          <Chip
                            label={`${parseFloat(item.totalQty).toFixed(2)} ${item.UOM || 'EA'}`}
                            size="small"
                            sx={{
                              backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              color: PRIMARY_COLOR,
                              fontWeight: 700,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            placeholder="0"
                            value={excessQties[item.ItemID] || 0}
                            onChange={(e) => {
                              const newValue = parseFloat(e.target.value) || 0;
                              // Allow adjustment but ensure Final Qty cannot be negative
                              // Constraint: Indented Qty + Adjustment Qty >= 0
                              // Therefore: Adjustment Qty >= -Indented Qty
                              const minAdjustment = -parseFloat(item.totalQty);
                              if (newValue >= minAdjustment) {
                                setExcessQties(prev => ({ ...prev, [item.ItemID]: newValue }));
                              }
                            }}
                            error={parseFloat(excessQties[item.ItemID] || 0) < -parseFloat(item.totalQty)}
                            helperText={parseFloat(excessQties[item.ItemID] || 0) < -parseFloat(item.totalQty) ? 'Cannot be less than -' + parseFloat(item.totalQty).toFixed(2) : ''}
                            sx={{ width: 'auto', maxWidth: '100px' }}
                            inputProps={{ step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const indentedQty = parseFloat(item.totalQty);
                            const adjustmentQty = parseFloat(excessQties[item.ItemID] || 0);
                            const finalQty = Math.max(0, indentedQty + adjustmentQty);
                            return (
                              <Chip
                                label={`${parseFloat(finalQty).toFixed(2)} ${item.UOM || 'EA'}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#E8F5E9',
                                  color: '#1B5E20',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  border: `1px solid ${PRIMARY_COLOR}`
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        {/* <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            placeholder="Rate"
                            value={rates[item.ItemID] || ''}
                            onChange={(e) => setRates(prev => ({ ...prev, [item.ItemID]: parseFloat(e.target.value) || 0 }))}
                            sx={{ width: '70px' }}
                            inputProps={{ step: 0.01, style: { textAlign: 'center' } }}
                          />
                        </TableCell> */}
                        {/* <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            placeholder="Tax %"
                            value={taxRates[item.ItemID] || ''}
                            onChange={(e) => setTaxRates(prev => ({ ...prev, [item.ItemID]: parseFloat(e.target.value) || 0 }))}
                            sx={{ width: '70px' }}
                            inputProps={{ step: 0.01, style: { textAlign: 'center' } }}
                          />
                        </TableCell> */}
                        {/* <TableCell align="center">
                          {(() => {
                            const indentedQty = parseInt(item.totalQty);
                            const adjustmentQty = parseInt(excessQties[item.ItemID] || 0);
                            const finalQty = Math.max(0, indentedQty + adjustmentQty);
                            const rate = parseFloat(rates[item.ItemID] || 0);
                            const taxRate = parseFloat(taxRates[item.ItemID] || 0);
                            const subtotal = finalQty * rate;
                            const tax = subtotal * (taxRate / 100);
                            const totalAmount = subtotal + tax;
                            return (
                              <Chip
                                label={totalAmount.toFixed(2)}
                                size="small"
                                sx={{
                                  backgroundColor: '#FFF9C4',
                                  color: '#F57F17',
                                  fontWeight: 700,
                                  fontSize: '0.75rem',
                                  border: `1px solid #F57F17`
                                }}
                              />
                            );
                          })()}
                        </TableCell> */}
                        <TableCell align="center">
                          {editingVendor === item.ItemID ? (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <Select
                                value={vendorIds[item.ItemID] || (vendorList.length > 0 ? vendorList[0].customerCode || vendorList[0].branchID : '')}
                                onChange={(e) => {
                                  setVendorIds(prev => ({ ...prev, [item.ItemID]: e.target.value }));
                                  setEditingVendor(null);
                                }}
                                onBlur={() => setEditingVendor(null)}
                                autoFocus
                                sx={{ textAlign: 'left' }}
                              >
                                {vendorList.map((vendor) => (
                                  <MenuItem
                                    key={vendor.customerCode || vendor.branchID}
                                    value={vendor.customerCode || vendor.branchID}
                                  >
                                    {vendor.customerCode || vendor.branchID} - {vendor.customerName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={(() => {
                                const vendorId = vendorIds[item.ItemID];
                                const vendor = vendorList.find(v => (v.customerCode || v.branchID) === vendorId);
                                return vendor ? `${vendor.customerCode || vendor.branchID} - ${vendor.customerName}` : 'Select Vendor';
                              })()}
                              size="small"
                              onClick={() => setEditingVendor(item.ItemID)}
                              sx={{
                                backgroundColor: vendorIds[item.ItemID] ? '#FF6B6B' : 'darkslategrey',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                                maxWidth: '250px',
                                '&:hover': {
                                  opacity: 0.8,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Drill-down: Show indents for this item */}
                      {expandedItemId === item.ItemID && (
                        <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.02) }}>
                          <TableCell colSpan={7} sx={{ py: 1, px: 2 }}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: PRIMARY_COLOR, display: 'block', mb: 1 }}>
                                Selected Indents ({item.indents.length})
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                {item.indents.map((indent, indentIdx) => {
                                  const indentDateString = dayjs(indent.IndentDate || new Date()).format('YYYYMMDD');
                                  const uniqueIndentDrilldownKey = `${indent.indentKey}|${indentDateString}|${item.ItemID}-${indentIdx}`;
                                  return (
                                  <Box
                                    key={uniqueIndentDrilldownKey}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      p: 1,
                                      backgroundColor: '#fff',
                                      borderRadius: 1,
                                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.2)}`,
                                      justifyContent: 'flex-start'
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedItems[`${indent.indentKey}|${item.ItemID}`] || false}
                                      disabled
                                      size="small"
                                      sx={{
                                        color: '#cccccc',
                                        '&.Mui-checked': { color: '#999999' },
                                        '&.Mui-disabled': {
                                          color: '#cccccc',
                                          opacity: 0.6,
                                          cursor: 'not-allowed'
                                        },
                                        flexShrink: 0
                                      }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                        {indent.BranchName} - Indent #{indent.IndentID}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        Qty: {indent.Qty} {indent.UOM || 'EA'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1 }} />

            {/* Action Buttons */}
            <Box display="flex" gap={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download sx={{ fontSize: '1rem' }} />}
                onClick={() => handleExportConsolidated(consolidatedItems)}
                disabled={isPlacingOrder}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderColor: PRIMARY_COLOR,
                  color: PRIMARY_COLOR,
                  py: 0.75,
                  px: 2,
                  '&:hover': {
                    backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                    borderColor: PRIMARY_COLOR,
                  }
                }}
              >
                Export CSV
              </Button>
              {/* <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: '#FF9800',
                  color: '#FF9800',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  py: 0.75,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 152, 0, 0.05)',
                    borderColor: '#FF9800',
                  }
                }}
                onClick={onFetchPOList}
                disabled={isPlacingOrder || loadingPOList}
                startIcon={loadingPOList ? <CircularProgress size={16} color="inherit" /> : <ShoppingCart sx={{ fontSize: '1rem' }} />}
              >
                {loadingPOList ? 'Loading...' : 'Created PO'}
              </Button> */}
              <Button
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: PRIMARY_COLOR,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  py: 0.75,
                  px: 2.5,
                  '&:hover': { backgroundColor: '#003300' },
                  '&:disabled': { backgroundColor: alpha(PRIMARY_COLOR, 0.5) }
                }}
                onClick={() => onPlaceOrder(consolidatedItems, excessQties, vendorIds, rates, taxRates, taxCategoryIds, taxStructureCodes)}
                disabled={isPlacingOrder || consolidatedItems.length === 0}
                startIcon={isPlacingOrder ? <CircularProgress size={16} color="inherit" /> : <CheckCircle sx={{ fontSize: '1rem' }} />}
              >
                {isPlacingOrder ? 'Placing Order...' : 'Place Consolidated Order'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== EXPORT HELPER ====================
const handleExportConsolidated = (items) => {
  const csvContent = [
    ['Item ID', 'Item Name', 'Total Qty', 'Consolidated Date'].join(','),
    ...items.map((item) =>
      [
        item.ItemID,
        `"${item.ItemName}"`,
        item.totalQty,
        dayjs().format('DD-MMM-YYYY'),
      ].join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `consolidated-order-${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ==================== CONFIRMATION DIALOG ====================
const ConfirmationDialog = ({ open, title, message, onConfirm, onCancel, isLoading }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography variant="body2">{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isLoading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        sx={{ backgroundColor: PRIMARY_COLOR }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={20} /> : 'Confirm'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ==================== MAIN COMPONENT ====================
export const IndentConsolidationDashboard = () => {
  // Component mount log
  console.log('🚀 IndentConsolidationDashboard loaded');

  // State
  const [filters, setFilters] = useState({
    area: '',
    fromDate: dayjs().subtract(0, 'days'),
    toDate: dayjs(),
  });

  const [branches, setBranches] = useState([]);
  const [selectedIndents, setSelectedIndents] = useState([]);
  const [expandedIndent, setExpandedIndent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);  // 0 = Indents, 1 = Summary
  const [selectedItems, setSelectedItems] = useState({});  // Track selected items within indents
  const [poList, setPoList] = useState([]);  // Store fetched PO list
  const [loadingPOList, setLoadingPOList] = useState(false);  // Loading state for PO list
  const [showPODetails, setShowPODetails] = useState(false);  // Show/hide PO details modal
  const [selectedPOId, setSelectedPOId] = useState(null);  // Selected PO ID for details
  const [poDetails, setPoDetails] = useState(null);  // Store fetched PO details
  const [loadingPODetails, setLoadingPODetails] = useState(false);  // Loading state for PO details
  const [vendorList, setVendorList] = useState([]);  // Store vendor list
  const [areas, setAreas] = useState([]);  // Store area list
  const [loadingAreas, setLoadingAreas] = useState(false);  // Loading state for areas

  // Get branchId from localStorage
  const branchId = localStorage.getItem("branchId");

  // Fetch vendor list with DCBranchId
  const fetchVendorList = useCallback(async (dcBranchId) => {
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const url = `${local_url}/api/customer/${dcBranchId}/LV`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch vendors: ${response.status}`);
      }

      const data = await response.json();
      const vendors = data.branches || [];
      setVendorList(vendors);
      console.log('✅ Vendor list fetched with DCBranchId:', { dcBranchId, vendorCount: vendors.length });
    } catch (error) {
      console.error('Error fetching vendor list:', error);
    }
  }, []);

  // Initialize vendor list on component mount
  React.useEffect(() => {
    const defaultBranchId = localStorage.getItem("branchId");
    if (defaultBranchId) {
      fetchVendorList(defaultBranchId);
    }
  }, [fetchVendorList]);

  // Fetch areas list
  const fetchAreas = useCallback(async () => {
    setLoadingAreas(true);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const url = `${local_signage}/api/v1/sfareabysegment?SegmentCode=LAZIZ`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch areas: ${response.status}`);
      }

      const data = await response.json();
      const areasList = Array.isArray(data) ? data : data.areas || [];
      setAreas(areasList);
      console.log('✅ Areas fetched:', { areaCount: areasList.length, areas: areasList });
    } catch (error) {
      console.error('Error fetching areas:', error);
      setAreas([]);
    } finally {
      setLoadingAreas(false);
    }
  }, []);

  // Initialize areas on component mount
  React.useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  // Handler: Filter change
  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handler: Apply filters (fetch data)
  const handleApplyFilters = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const areaId = filters.area || '00001';
      const fromDate = dayjs(filters.fromDate).format('YYYYMMDD');
      const toDate = dayjs(filters.toDate).format('YYYYMMDD');

      const url = `${local_signage}/api/v1/getjsonareawiseindent?AreaId=${encodeURIComponent(areaId)}&FromDate=${fromDate}&ToDate=${toDate}`;

      const token = localStorage.getItem('AuthorizationToken');
      const headers = {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      };

      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      const branchesData = data?.AreaWiseIndent || [];

      // Extract DCBranchId from the first area's data
      let dcBranchId = localStorage.getItem("branchId");
      if (branchesData.length > 0 && branchesData[0].DCBranchId) {
        dcBranchId = branchesData[0].DCBranchId;
      }

      // Log data structure for debugging
      console.log('📊 Data loaded:', {
        branchCount: branchesData.length,
        dcBranchId: dcBranchId,
        branches: branchesData.map(b => ({
          BranchId: b.BranchId,
          BranchName: b.BranchName,
          DCBranchId: b.DCBranchId,
          docCount: Array.isArray(b.doc) ? b.doc.length : 0,
          indents: Array.isArray(b.doc) ? b.doc.map(d => ({
            BranchId: d.BranchId,
            IndentID: d.IndentID,
            itemCount: Array.isArray(d.line) ? d.line.length : 0
          })) : []
        }))
      });

      setBranches(branchesData);
      setSelectedIndents([]);
      setExpandedIndent(null);
      setSelectedItems({});

      // Fetch vendor list with DCBranchId
      await fetchVendorList(dcBranchId);

      setSuccess('Data loaded successfully');
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, fetchVendorList]);

  // Handler: Select item
  const handleSelectItem = useCallback((itemKey, isChecked) => {
    console.log('🔵 handleSelectItem:', { itemKey, isChecked });

    setSelectedItems((prev) => {
      const updated = {
        ...prev,
        [itemKey]: isChecked
      };

      // Calculate selection summary
      const selectedItemKeys = Object.keys(updated).filter(key => updated[key]);

      // Group by indent and calculate totals
      const indentGroups = {};
      let totalQty = 0;
      const itemDetails = [];

      selectedItemKeys.forEach(key => {
        const [branchId, indentId, itemId] = key.split('|');
        const indentKey = `${branchId}|${indentId}`;

        // Find the item in branches to get quantity
        let itemQty = 0;
        let itemName = '';
        for (const branch of branches) {
          const docs = Array.isArray(branch.doc) ? branch.doc : [];
          for (const doc of docs) {
            if (doc.BranchId === branchId && doc.IndentID === indentId) {
              const lines = Array.isArray(doc.line) ? doc.line : [];
              const line = lines.find(l => l.ItemID === itemId);
              if (line) {
                itemQty = line.Qty || 0;
                itemName = line.ItemName || '';
                break;
              }
            }
          }
          if (itemQty) break;
        }

        totalQty += itemQty;
        itemDetails.push({
          itemKey: key,
          itemId,
          itemName,
          qty: itemQty,
          indentKey
        });

        if (!indentGroups[indentKey]) {
          indentGroups[indentKey] = { items: [], totalQty: 0 };
        }
        indentGroups[indentKey].items.push({ itemId, itemName, qty: itemQty });
        indentGroups[indentKey].totalQty += itemQty;
      });

      console.log('📊 SELECTION SUMMARY:', {
        totalSelectedItems: selectedItemKeys.length,
        totalQty,
        indentGroups,
        itemDetails
      });

      return updated;
    });

    // Extract indent key from item key (format: branchId|indentId|itemId)
    const parts = itemKey.split('|');
    if (parts.length >= 3) {
      const indentKey = `${parts[0]}|${parts[1]}`;

      // If selecting an item, ensure its indent is in selectedIndents
      if (isChecked) {
        setSelectedIndents((prev) => {
          const updated = prev.includes(indentKey) ? prev : [...prev, indentKey];
          console.log('🔵 Updated selectedIndents:', updated);
          return updated;
        });
      }
      // Note: Don't auto-remove indent when deselecting a single item
      // User might still have other items selected in that indent
    }
  }, [branches]);

  // Handler: Select multiple items at once
  const handleSelectMultipleItems = useCallback((itemUpdates) => {
    console.log('🟢 handleSelectMultipleItems:', {
      itemUpdates,
      itemCount: Object.keys(itemUpdates).length
    });

    setSelectedItems((prev) => {
      const updated = {
        ...prev,
        ...itemUpdates
      };

      // Calculate selection summary
      const selectedItemKeys = Object.keys(updated).filter(key => updated[key]);

      // Group by indent and calculate totals
      const indentGroups = {};
      let totalQty = 0;
      const itemDetails = [];

      selectedItemKeys.forEach(key => {
        const [branchId, indentId, itemId] = key.split('|');
        const indentKey = `${branchId}|${indentId}`;

        // Find the item in branches to get quantity
        let itemQty = 0;
        let itemName = '';
        for (const branch of branches) {
          const docs = Array.isArray(branch.doc) ? branch.doc : [];
          for (const doc of docs) {
            if (doc.BranchId === branchId && doc.IndentID === indentId) {
              const lines = Array.isArray(doc.line) ? doc.line : [];
              const line = lines.find(l => l.ItemID === itemId);
              if (line) {
                itemQty = line.Qty || 0;
                itemName = line.ItemName || '';
                break;
              }
            }
          }
          if (itemQty) break;
        }

        totalQty += itemQty;
        itemDetails.push({
          itemKey: key,
          itemId,
          itemName,
          qty: itemQty,
          indentKey
        });

        if (!indentGroups[indentKey]) {
          indentGroups[indentKey] = { items: [], totalQty: 0 };
        }
        indentGroups[indentKey].items.push({ itemId, itemName, qty: itemQty });
        indentGroups[indentKey].totalQty += itemQty;
      });

      console.log('📊 SELECTION SUMMARY (BATCH):', {
        totalSelectedItems: selectedItemKeys.length,
        totalQty,
        indentGroups,
        itemDetails
      });

      return updated;
    });

    // Auto-add indents for selected items
    const indentsToAdd = new Set();
    Object.entries(itemUpdates).forEach(([itemKey, isChecked]) => {
      if (isChecked) {
        const parts = itemKey.split('|');
        if (parts.length >= 3) {
          indentsToAdd.add(`${parts[0]}|${parts[1]}`);
        }
      }
    });

    if (indentsToAdd.size > 0) {
      setSelectedIndents((prev) => {
        const newIndents = [...prev];
        indentsToAdd.forEach((indentKey) => {
          if (!newIndents.includes(indentKey)) {
            newIndents.push(indentKey);
          }
        });
        console.log('🟢 Updated selectedIndents (batch):', newIndents);
        return newIndents;
      });
    }
  }, [branches]);

  // Handler: Select indent (toggle all items in indent)
  const handleSelectIndent = useCallback((indentKey, isChecked) => {
    console.log('🟡 handleSelectIndent:', { indentKey, isChecked });

    // When checking indent checkbox, select all items in that indent
    // When unchecking, deselect all items in that indent
    const [branchId, indentId] = indentKey.split('|');

    // Search through all branches to find the indent
    let foundBranch = null;
    let foundIndent = null;

    for (const branch of branches) {
      const docs = Array.isArray(branch.doc) ? branch.doc : [];
      for (const doc of docs) {
        // Match by both BranchId and IndentID
        if (doc.BranchId === branchId && doc.IndentID === indentId) {
          foundBranch = branch;
          foundIndent = doc;
          break;
        }
      }
      if (foundIndent) break;
    }

    if (foundIndent) {
      const lines = Array.isArray(foundIndent.line) ? foundIndent.line : [];
      const itemUpdates = {};
      lines.forEach((line) => {
        itemUpdates[`${indentKey}|${line.ItemID}`] = isChecked;
      });

      console.log('🟡 Items to update:', {
        indentKey,
        itemCount: lines.length,
        itemUpdates: Object.keys(itemUpdates)
      });

      handleSelectMultipleItems(itemUpdates);

      // Add/remove indent from selectedIndents based on whether any items are selected
      if (isChecked) {
        setSelectedIndents((prev) => {
          const updated = prev.includes(indentKey) ? prev : [...prev, indentKey];
          console.log('🟡 Updated selectedIndents (add):', updated);
          return updated;
        });
      } else {
        setSelectedIndents((prev) => {
          const updated = prev.filter((key) => key !== indentKey);
          console.log('🟡 Updated selectedIndents (remove):', updated);
          return updated;
        });
      }
    } else {
      console.error('❌ Indent not found:', { branchId, indentId, indentKey });
    }
  }, [branches, handleSelectMultipleItems]);

  // Handler: Select all indents
  const handleSelectAllIndents = useCallback((indentKeys) => {
    setSelectedIndents(indentKeys);
  }, []);

  // Handler: Place order
  const handlePlaceOrder = useCallback(async (consolidatedItems, excessQties, vendorIds, rates, taxRates, taxCategoryIds, taxStructureCodes) => {
    if (selectedIndents.length === 0) {
      setError('Please select at least one indent');
      return;
    }

    // Store these in state or ref so they can be accessed in confirm handler
    window.consolidatedOrderData = { consolidatedItems, excessQties, vendorIds, rates, taxRates, taxCategoryIds, taxStructureCodes };
    setShowConfirmDialog(true);
  }, [selectedIndents]);

  // Handler: Confirm place order
  const handleConfirmPlaceOrder = useCallback(async () => {
    setIsPlacingOrder(true);
    setError(null);

    try {
      // Retrieve the data stored when Place Order was clicked
      const { consolidatedItems, excessQties, vendorIds, rates = {}, taxRates = {}, taxCategoryIds = {}, taxStructureCodes = {} } = window.consolidatedOrderData || { consolidatedItems: [], excessQties: {}, vendorIds: {}, rates: {}, taxRates: {}, taxCategoryIds: {}, taxStructureCodes: {} };

      // Build Line array (consolidated items with Final Qty, Rate, TaxRate, and TotalAmount)
      const lineArray = consolidatedItems.map((item) => {
        const indentedQty = parseFloat(item.totalQty);
        const adjustmentQty = parseFloat(excessQties[item.ItemID] || 0);
        const finalQty = Math.max(0, indentedQty + adjustmentQty);
        const rate = parseFloat(rates[item.ItemID] || 0);
        const taxRate = parseFloat(taxRates[item.ItemID] || 0);
        const subtotal = finalQty * rate;
        const tax = subtotal * (taxRate / 100);
        const totalAmount = subtotal + tax;

        return {
          ItemId: item.ItemID,
          VendorId: vendorIds[item.ItemID] || "R608", // Use custom vendor if set, otherwise default to R608
          Remarks: "Urgent",
          Qty: parseFloat(finalQty.toFixed(2)),
          AltQty: 0,
          Rate: 1,
          TaxRate: 1,
          TaxCategoryId: taxCategoryIds[item.ItemID] || "TX01",
          TaxStructureCode: taxStructureCodes[item.ItemID] || "TS01",
          TotalAmount: 2
        };
      });

      // Build LineDet array (individual indent items)
      const lineDetArray = [];
      selectedIndents.forEach((indentKey) => {
        const [indentBranchId, indentId] = indentKey.split('|');
        console.log('🔍 Building LineDet for indent:', { indentKey, indentBranchId, indentId });

        // Find the indent by matching BOTH BranchId AND IndentID
        let foundIndent = null;
        let foundBranch = null;
        for (const branch of branches) {
          const docs = Array.isArray(branch.doc) ? branch.doc : [];
          for (const doc of docs) {
            // CRITICAL: Match by BOTH BranchId AND IndentID
            if (doc.BranchId === indentBranchId && doc.IndentID === indentId) {
              foundIndent = doc;
              foundBranch = branch;
              break;
            }
          }
          if (foundIndent) break;
        }

        if (!foundIndent) {
          console.log('❌ Indent not found for LineDet:', { indentKey, indentBranchId, indentId });
          return;
        }

        console.log('✅ Found indent for LineDet:', {
          indentKey,
          foundIndentBranchId: foundIndent.BranchId,
          foundIndentID: foundIndent.IndentID,
          itemCount: Array.isArray(foundIndent.line) ? foundIndent.line.length : 0
        });

        const lines = Array.isArray(foundIndent.line) ? foundIndent.line : [];
        lines.forEach((line) => {
          // Only include selected items
          const itemKey = `${indentKey}|${line.ItemID}`;
          if (!selectedItems[itemKey]) return;

          const lineDetItem = {
            BranchId: foundIndent.BranchId || indentBranchId,
            IndentId: indentId,
            ItemId: line.ItemID,
            Qty: line.Qty || 0,
            AltQty: 0
          };

          console.log('➕ Adding to LineDet:', lineDetItem);
          lineDetArray.push(lineDetItem);
        });
      });

      console.log('📋 LineDet Array Built:', {
        totalItems: lineDetArray.length,
        items: lineDetArray
      });

      console.log()

      // Extract DCBranchId from branches data
      let dcBranchId = branchId; // Fallback to localStorage branchId
      if (branches.length > 0 && branches[0].DCBranchId) {
        dcBranchId = branches[0].DCBranchId;
      }

      console.log('🔗 Using DCBranchId for payload:', { dcBranchId, fallbackBranchId: branchId });

      // Build final payload
      const payload = {
        Option: "1",
        BranchID: dcBranchId,
        TransTypeID: "PO",
        OrderDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
        DeliveryDate: dayjs().add(7, 'days').format('YYYY-MM-DDTHH:mm:ss'),
        Remarks: "Purchase Order for consolidated indent",
        CreatedUserId: "10024004",
        Line: lineArray,
        LineDet: lineDetArray
      };

      console.log('📦 FINAL PAYLOAD:', {
        lineCount: lineArray.length,
        lineDetCount: lineDetArray.length,
        payload: payload
      });

      console.log('📤 Sending POST request to API...');

      const token = localStorage.getItem('AuthorizationToken');
      const headers = {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${local_signage}/api/v1/insertindenttopo`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      console.log('📥 API Response:', {
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Order placed successfully:', result);

      setSuccess('Consolidated order placed successfully!');
      setSelectedIndents([]);
      setSelectedItems({});
      setShowConfirmDialog(false);

      // Re-fetch data after placing order
      setTimeout(() => handleApplyFilters(), 1500);
    } catch (err) {
      setError(`Failed to place order: ${err.message}`);
    } finally {
      setIsPlacingOrder(false);
    }
  }, [selectedIndents, branches, selectedItems, handleApplyFilters]);

  // Handler: Fetch PO List
  const handleFetchPOList = useCallback(async () => {
    setLoadingPOList(true);
    setError(null);

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const headers = {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      };
const Defaultbranch=localStorage.getItem("branchId");
      const response = await fetch(`${local_signage}/api/v1/fetchpolist?BranchId=${Defaultbranch}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();
      setPoList(Array.isArray(result) ? result : []);
    } catch (err) {
      setError(`Failed to fetch PO list: ${err.message}`);
    } finally {
      setLoadingPOList(false);
    }
  }, []);

  // Handler: Fetch PO Details
  const handleFetchPODetails = useCallback(async (orderId) => {
    setLoadingPODetails(true);
    setError(null);

    try {
      const token = localStorage.getItem('AuthorizationToken');
      const headers = {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${local_signage}/api/v1/fetchpobyid?OrderId=${orderId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();
      setPoDetails(result);
      setShowPODetails(true);
    } catch (err) {
      setError(`Failed to fetch PO details: ${err.message}`);
    } finally {
      setLoadingPODetails(false);
    }
  }, []);

  // Auto-fetch PO list when Created PO tab is opened
  React.useEffect(() => {
    if (activeTab === 2 && poList.length === 0) {
      handleFetchPOList();
    }
  }, [activeTab, poList.length, handleFetchPOList]);

  // Render
  return (
    <>
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
         <Header/>
        <Box
          sx={{
            height: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              py: 1.5,
              px: 1.5,
            }}
          >
            <Container maxWidth="xl" disableGutters>
              <Stack spacing={1.5}>
                {/* Header */}
               
                {/* <Fade in timeout={300}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                      <TrendingUp sx={{ fontSize: 28, color: PRIMARY_COLOR }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR, fontSize: '1rem' }}>
                        Central Indent Consolidation
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 5, fontSize: '0.75rem' }}>
                      Consolidate branch indents and place central orders
                    </Typography>
                  </Box>
                </Fade> */}

                {/* Alerts */}
                {error && (
                  <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setError(null)}
                      >
                        <Close fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert
                    severity="success"
                    onClose={() => setSuccess(null)}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setSuccess(null)}
                      >
                        <Close fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    {success}
                  </Alert>
                )}

                {/* Filter Section */}
                <FilterBar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onApply={handleApplyFilters}
                  isLoading={isLoading}
                  areas={areas}
                  loadingAreas={loadingAreas}
                />

                {/* Tabbed Interface */}
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0, 77, 0, 0.08)', mb: 1.5 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: alpha(PRIMARY_COLOR, 0.02) }}>
                    <Tabs
                      value={activeTab}
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      sx={{
                        minHeight: 48,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: '#666',
                          py: 1,
                          minHeight: 48,
                          '&.Mui-selected': {
                            color: PRIMARY_COLOR,
                          },
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: PRIMARY_COLOR,
                          height: 2.5,
                        },
                      }}
                    >
                      <Tab
                        label={`Indent (${selectedIndents.length})`}
                        icon={<LocalShipping sx={{ mr: 0.8, fontSize: '1.1rem' }} />}
                        iconPosition="start"
                      />
                      <Tab
                        label={`Summary (${selectedIndents.length > 0 ? 'Ready' : 'Empty'})`}
                        icon={<CheckCircle sx={{ mr: 0.8, fontSize: '1.1rem' }} />}
                        iconPosition="start"
                      />
                      <Tab
                        label={`Created PO (${poList.length})`}
                        icon={<ShoppingCart sx={{ mr: 0.8, fontSize: '1.1rem' }} />}
                        iconPosition="start"
                      />
                    </Tabs>
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    {/* Tab 1: Indent Summary Table */}
                    {activeTab === 0 && (
                      <Box sx={{ position: 'relative' }}>
                        <IndentSummaryTable
                          branches={branches}
                          selectedIndents={selectedIndents}
                          onSelectIndent={handleSelectIndent}
                          onSelectAllIndents={handleSelectAllIndents}
                          expandedIndent={expandedIndent}
                          onExpandIndent={setExpandedIndent}
                          selectedItems={selectedItems}
                          onSelectItem={handleSelectItem}
                          onSelectMultipleItems={handleSelectMultipleItems}
                        />

                        {/* Floating Next Button */}
                        {selectedIndents.length > 0 && (
                          <Fab
                            variant="extended"
                            sx={{
                              position: 'fixed',
                              bottom: 24,
                              right: 24,
                              backgroundColor: PRIMARY_COLOR,
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              py: 1,
                              px: 2,
                              '&:hover': {
                                backgroundColor: '#003300',
                                boxShadow: '0 6px 16px rgba(0, 77, 0, 0.3)',
                              },
                              zIndex: 100,
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  boxShadow: '0 0 0 0 rgba(0, 77, 0, 0.7)',
                                },
                                '50%': {
                                  boxShadow: '0 0 0 8px rgba(0, 77, 0, 0)',
                                },
                              },
                            }}
                            onClick={() => setActiveTab(1)}
                          >
                            Next
                            <ArrowForward sx={{ ml: 0.8, fontSize: '1rem' }} />
                          </Fab>
                        )}
                      </Box>
                    )}

                    {/* Tab 2: Consolidated Summary Section */}
                    {activeTab === 1 && (
                      <Box sx={{ p: 1.5 }}>
                        <ConsolidatedSummarySection
                          selectedIndents={selectedIndents}
                          branches={branches}
                          onPlaceOrder={handlePlaceOrder}
                          isPlacingOrder={isPlacingOrder}
                          onSelectIndent={handleSelectIndent}
                          selectedItems={selectedItems}
                          onSelectItem={handleSelectItem}
                          onFetchPOList={handleFetchPOList}
                          loadingPOList={loadingPOList}
                          vendorList={vendorList}
                        />
                      </Box>
                    )}

                    {/* Tab 3: Created PO List */}
                    {activeTab === 2 && (
                      <Box sx={{ p: 1.5 }}>
                        {loadingPOList ? (
                          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                            <CircularProgress />
                          </Box>
                        ) : poList.length === 0 ? (
                          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 5 }}>
                            No purchase orders found.
                          </Typography>
                        ) : (
                          <TableContainer
                            sx={{
                              border: `1px solid ${alpha(PRIMARY_COLOR, 0.2)}`,
                              borderRadius: 1,
                              maxHeight: '500px',
                              overflow: 'auto',
                              mb: 3,
                              '&::-webkit-scrollbar': {
                                width: '6px',
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                                borderRadius: '3px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: PRIMARY_COLOR,
                                borderRadius: '3px',
                                '&:hover': {
                                  backgroundColor: alpha(PRIMARY_COLOR, 0.8),
                                },
                              },
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.1) }}>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>PO ID</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Branch</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Type</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Remarks</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Order Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {poList.map((po, index) => (
                                  <TableRow
                                    key={index}
                                    hover
                                    onClick={() => {
                                      setSelectedPOId(po.OrderID);
                                      handleFetchPODetails(po.OrderID);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{po.OrderID}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{po.BranchName || po.BranchId}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{po.TransTypeID}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{po.Remarks}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                      {po.OrderDate ? new Date(po.OrderDate).toLocaleDateString('en-IN') : '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Stack>
              </Container>
            </Box>
          </Box>

        {/* PO Details Dialog */}
        <Dialog
          open={showPODetails}
          onClose={() => setShowPODetails(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem', backgroundColor: alpha(PRIMARY_COLOR, 0.1) }}>
            PO Details - {selectedPOId}
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            {loadingPODetails ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
              </Box>
            ) : poDetails ? (
              <Box>
                {/* Header Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: PRIMARY_COLOR }}>
                    Header Details
                  </Typography>
                  {poDetails.headerDetails && poDetails.headerDetails.length > 0 && (
                    <Paper sx={{ p: 2, backgroundColor: alpha(PRIMARY_COLOR, 0.02) }}>
                      <Grid container spacing={2}>
                        {(() => {
                          const header = poDetails.headerDetails[0];
                          return (
                            <>
                              <Grid item xs={12} sm={6}><Typography><strong>Order ID:</strong> {header.OrderID}</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Branch:</strong> {header.BranchName} ({header.BranchId})</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Type:</strong> {header.TransTypeID}</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Vendor:</strong> {header.VendorName} ({header.VendorId})</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Order Date:</strong> {new Date(header.OrderDate).toLocaleDateString('en-IN')}</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Delivery Date:</strong> {new Date(header.DeliveryDate).toLocaleDateString('en-IN')}</Typography></Grid>
                              <Grid item xs={12}><Typography><strong>Remarks:</strong> {header.Remarks}</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Posted:</strong> {header.Posted ? 'Yes' : 'No'}</Typography></Grid>
                              <Grid item xs={12} sm={6}><Typography><strong>Closed:</strong> {header.Closed ? 'Yes' : 'No'}</Typography></Grid>
                            </>
                          );
                        })()}
                      </Grid>
                    </Paper>
                  )}
                </Box>

                {/* Item Details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: PRIMARY_COLOR }}>
                    Item Details ({poDetails.itemDetails?.length || 0} items)
                  </Typography>
                  {poDetails.itemDetails && poDetails.itemDetails.length > 0 ? (
                    <TableContainer sx={{ border: `1px solid ${alpha(PRIMARY_COLOR, 0.2)}`, borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600 }}>Item ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Item Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {poDetails.itemDetails.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{item.ItemID}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{item.ItemName}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }} align="right">{item.Qty}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{item.Remarks}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="textSecondary">No items found</Typography>
                  )}
                </Box>

                {/* Ref Indent Details */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: PRIMARY_COLOR }}>
                    Reference Indent Details ({poDetails.refIndentDetails?.length || 0} references)
                  </Typography>
                  {poDetails.refIndentDetails && poDetails.refIndentDetails.length > 0 ? (
                    <TableContainer sx={{ border: `1px solid ${alpha(PRIMARY_COLOR, 0.2)}`, borderRadius: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(PRIMARY_COLOR, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600 }}>Indent ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Ref Branch</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Item ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {poDetails.refIndentDetails.map((ref, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{ref.RefIndentId}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{ref.BranchName}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{ref.ItemId}</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }} align="right">{ref.Qty}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="textSecondary">No reference indents found</Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography color="textSecondary">No details available</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, backgroundColor: alpha(PRIMARY_COLOR, 0.02) }}>
            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#FF9800',
                color: '#FF9800',
                textTransform: 'none',
                fontWeight: 600,
              }}
              onClick={() => {
                if (poDetails) {
                  // Export as CSV
                  const csvContent = [
                    ['Purchase Order Details'],
                    ['PO ID', selectedPOId],
                    [],
                    ['HEADER DETAILS'],
                    ...poDetails.headerDetails.map(h => [
                      `Order ID: ${h.OrderID}`,
                      `Branch: ${h.BranchName}`,
                      `Vendor: ${h.VendorName}`,
                      `Order Date: ${new Date(h.OrderDate).toLocaleDateString('en-IN')}`
                    ]),
                    [],
                    ['ITEM DETAILS'],
                    ['Item ID', 'Item Name', 'Qty', 'Remarks'],
                    ...poDetails.itemDetails.map(i => [i.ItemID, i.ItemName, i.Qty, i.Remarks]),
                    [],
                    ['REFERENCE INDENT DETAILS'],
                    ['Indent ID', 'Branch', 'Item ID', 'Qty'],
                    ...poDetails.refIndentDetails.map(r => [r.RefIndentId, r.BranchName, r.ItemId, r.Qty])
                  ].map(row => Array.isArray(row) ? row.join(',') : row).join('\n');

                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `PO_${selectedPOId}.csv`;
                  link.click();
                }
              }}
            >
              Export CSV
            </Button>
            <Button
              onClick={() => setShowPODetails(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: PRIMARY_COLOR,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={showConfirmDialog}
          title="Confirm Consolidated Order"
          message={`Are you sure you want to place a consolidated order from ${selectedIndents.length} selected indent(s)? This action cannot be undone.`}
          onConfirm={handleConfirmPlaceOrder}
          onCancel={() => setShowConfirmDialog(false)}
          isLoading={isPlacingOrder}
        />
      </LocalizationProvider>
    </ThemeProvider>
    </>
  );
};

export default IndentConsolidationDashboard;